/**
 * Automatische Ermittlung der Notfallstellen (Krankenhaus, Durchgangsarzt,
 * Augenarzt, Apotheke) zur Baumaßnahme-Adresse.
 *
 * Ausgelagert aus dem Notrufplan-Editor, weil der Anlage-Assistent denselben
 * Ablauf braucht („Notrufplan automatisch befüllen") — die Logik darf nicht an
 * einem Formularzustand hängen. Netzzugriff bleibt hier, der Zustand bleibt in
 * den Komponenten, die Bewertungsregeln in services/notfallStellen.ts.
 *
 * ─── Die Reihenfolge ist die ganze Beschleunigung ──────────────────────────
 *   1. Adresse verorten (Nominatim, eine Anfrage).
 *   2. GEPRÜFTE Stellen im Umkreis aus der Datenbank holen (eine Abfrage).
 *      Was ein Mitarbeiter bestätigt hat, gilt sofort und ohne Rückfrage.
 *   3. Nur für die Kategorien, die dabei offen blieben, EINE Overpass-Abfrage.
 *      Sind alle vier beantwortet, entfällt dieser Schritt vollständig — das
 *      ist der Normalfall, sobald ein Gebiet einmal bearbeitet wurde.
 *   4. Telefonlücken: EIN Sammelaufruf an die KI statt bis zu vier einzelner.
 *   5. Karte zeichnen (optional, Fehler schadet dem Ergebnis nicht).
 *
 * Vorher lief Schritt 3 als bis zu sechs aufeinanderfolgende Overpass-Abfragen
 * mit je 1,1 s Zwangspause, und Schritt 4 als vier getrennte KI-Aufrufe.
 */

import { NotrufKontakt, NotrufplanInhalt } from './types';
import { autoZoom, buildStaticMap, geocode, KartenPunkt, MARKER_FARBE, sucheStellen } from '../../services/osmMaps';
import {
  KandidatenNachKategorie, KATEGORIEN, KATEGORIE_LABEL, LatLng, NotfallKategorie, StellenKandidat,
  SUCHRADIUS, besterVorschlag, entdoppele, leereKandidaten, offeneKategorien, sortiereKandidaten,
} from '../../services/notfallStellen';
import { ladeGeprueftesUmfeld } from '../../services/notfallStellenDb';
import { chatSigePlan } from '../../services/geminiService';

/** Die Kategorien in der Reihenfolge, in der sie im Notrufplan stehen. */
const FELD: Record<NotfallKategorie, keyof Pick<NotrufplanInhalt, 'krankenhaus' | 'durchgangsarzt' | 'augenarzt' | 'apotheke'>> = {
  krankenhaus: 'krankenhaus',
  durchgangsarzt: 'durchgangsarzt',
  augenarzt: 'augenarzt',
  apotheke: 'apotheke',
};

export interface NotrufAutoErgebnis {
  geo: { lat: number; lng: number };
  /** Nur gefundene Stellen sind gesetzt — nicht Gefundenes bleibt unangetastet. */
  kontakte: Partial<Pick<NotrufplanInhalt, 'krankenhaus' | 'durchgangsarzt' | 'augenarzt' | 'apotheke'>>;
  /**
   * Alle Treffer je Kategorie, bewertet und sortiert — die Auswahlliste der
   * Oberfläche. Der erste Eintrag ist der, der oben in `kontakte` steht.
   */
  kandidaten: KandidatenNachKategorie;
  /** Kartenpunkte neben dem Projekt — alle vier Kategorien, je mit Farbe und Kuerzel. */
  stellen: KartenPunkt[];
  zoom: number;
  kartenBild?: string;
  /** Fertig-Meldung samt Warnhinweisen für die Oberfläche. */
  status: string;
  /** Kategorien, die aus geprüften Datenbankeinträgen kamen (ohne Netzsuche). */
  ausDatenbank: NotfallKategorie[];
}

/**
 * Marker der Karte je Kategorie — Farbe UND Kuerzel an einem Ort, damit die
 * Legende im PDF (generatorSigePlan) und die Karte nie auseinanderlaufen.
 */
export const KARTEN_MARKER = [
  { kategorie: 'krankenhaus' as NotfallKategorie, farbe: MARKER_FARBE.krankenhaus, kuerzel: 'K', label: 'Krankenhaus' },
  { kategorie: 'durchgangsarzt' as NotfallKategorie, farbe: MARKER_FARBE.durchgangsarzt, kuerzel: 'D', label: 'Durchgangsarzt' },
  { kategorie: 'augenarzt' as NotfallKategorie, farbe: MARKER_FARBE.augenarzt, kuerzel: 'A', label: 'Augenarzt' },
  { kategorie: 'apotheke' as NotfallKategorie, farbe: MARKER_FARBE.apotheke, kuerzel: 'Ap', label: 'Apotheke' },
] as const;

/** Meter -> Kilometer mit einer Nachkommastelle. */
const km = (m: number) => Math.round(m / 100) / 10;

export const kandidatZuKontakt = (s: StellenKandidat): NotrufKontakt => ({
  name: s.name,
  adresse: s.adresse,
  telefon: s.telefon,
  entfernung: km(s.entfernung),
  lat: s.lat,
  lng: s.lng,
  osmTyp: s.osmTyp,
  osmId: s.osmId,
  quelle: s.quelle,
  geprueftVon: s.geprueftVon,
  geprueftAm: s.geprueftAm,
  hinweis: s.hinweis,
});

/**
 * Antwort des Sammelaufrufs zerlegen: eine Zeile je Nummer, `UNBEKANNT` zählt
 * als „nichts gefunden".
 *
 * Rein und exportiert, damit die Auswertung ohne Netz prüfbar ist — die KI ist
 * die unzuverlässigste Zutat im ganzen Ablauf, und was sie liefert, landet in
 * einem Sicherheitsdokument.
 */
export const leseTelefonAntwort = (antwort: string, anzahl: number): (string | null)[] => {
  const raus: (string | null)[] = new Array(anzahl).fill(null);
  for (const zeile of (antwort || '').split(/\r?\n/)) {
    const kopf = zeile.match(/^\s*(\d{1,2})\s*[.):\-]\s*(.+)$/);
    if (!kopf) continue;
    const index = parseInt(kopf[1], 10) - 1;
    if (index < 0 || index >= anzahl) continue;
    const rest = kopf[2].trim();
    if (/unbekannt|keine angabe|nicht gefunden/i.test(rest)) continue;
    const nummer = rest.match(/[+(]?\d[\d\s/().\-]{6,}/);
    if (nummer) raus[index] = nummer[0].trim();
  }
  return raus;
};

/**
 * Fehlende Telefonnummern in EINEM KI-Aufruf nachschlagen.
 *
 * Halluzinations-Risiko bleibt — das Ergebnis wird deshalb im Status als
 * KI-Schätzung ausgewiesen und in der Oberfläche zur Prüfung markiert. Ein
 * Fehlschlag ist unkritisch: dann bleibt das Feld leer.
 */
const telefoneNachschlagen = async (
  luecken: { kategorie: NotfallKategorie; kandidat: StellenKandidat }[],
): Promise<NotfallKategorie[]> => {
  if (!luecken.length) return [];
  const liste = luecken
    .map((l, i) => `${i + 1}. ${KATEGORIE_LABEL[l.kategorie]}: "${l.kandidat.name}", ${l.kandidat.adresse || 'Adresse unbekannt'}`)
    .join('\n');
  const msg =
    'Nenne die offiziellen Telefonnummern dieser Einrichtungen in Deutschland.\n' +
    'Antworte NUR mit einer Zeile je Nummer im Format "<Nummer>. <Telefonnummer>" ' +
    '(z. B. "1. +49 30 1234567") oder "<Nummer>. UNBEKANNT". Keine Erklärungen, keine weiteren Zeilen.\n\n' +
    liste;
  try {
    const res = await chatSigePlan(msg, { zweck: 'notruf-telefon' });
    const nummern = leseTelefonAntwort(res?.antwort || '', luecken.length);
    const gefuellt: NotfallKategorie[] = [];
    nummern.forEach((nummer, i) => {
      if (!nummer) return;
      luecken[i].kandidat.telefon = nummer;
      gefuellt.push(luecken[i].kategorie);
    });
    return gefuellt;
  } catch (e) {
    console.warn('Telefon-Nachschlag fehlgeschlagen', e);
    return [];
  }
};

/**
 * Ermittelt die Notfallstellen zur Adresse. Wirft, wenn keine Adresse vorliegt
 * oder die Adresse nicht lokalisiert werden kann — alles andere ist optional
 * und wird im `status` als Lücke gemeldet.
 */
export const ermittleNotrufplan = async (
  adresse: string | undefined,
  melde?: (text: string) => void,
): Promise<NotrufAutoErgebnis> => {
  if (!adresse?.trim()) throw new Error('Keine Baumaßnahme-Adresse hinterlegt.');

  melde?.('Adresse wird lokalisiert …');
  const center: LatLng = await geocode(adresse);

  melde?.('Geprüfte Stellen werden abgerufen …');
  const maxRadius = Math.max(...KATEGORIEN.map(k => SUCHRADIUS[k]));
  const umfeld = await ladeGeprueftesUmfeld(center, maxRadius);

  const offen = offeneKategorien(umfeld.kandidaten);
  const ausDatenbank = KATEGORIEN.filter(k => !offen.includes(k) && umfeld.kandidaten[k].length > 0);

  // Ein Fehlschlag der Umkreissuche darf die geprüften Treffer nicht mitreißen:
  // wer drei bestaetigte Stellen hat und nur die vierte suchen liess, soll die
  // drei behalten. Steht danach überhaupt nichts, wird der Fehler geworfen —
  // „nichts gefunden" darf sich nie wie ein normales Ergebnis anfühlen.
  let gesucht: KandidatenNachKategorie = leereKandidaten();
  let suchFehler = '';
  if (offen.length) {
    melde?.(`Umkreissuche für ${offen.map(k => KATEGORIE_LABEL[k]).join(', ')} (OpenStreetMap) …`);
    try {
      gesucht = await sucheStellen(center, offen, umfeld.gesperrt);
    } catch (e) {
      suchFehler = e instanceof Error ? e.message : 'Umkreissuche fehlgeschlagen.';
      console.warn('Umkreissuche fehlgeschlagen', e);
    }
  } else {
    melde?.('Alle Stellen aus den geprüften Einträgen übernommen.');
  }

  // Geprüfte und gesuchte Treffer in EINE Liste je Kategorie — Dubletten
  // (dasselbe OSM-Objekt aus beiden Quellen) fallen dabei auf den geprüften
  // Eintrag zusammen, weil der die verlässlicheren Angaben trägt.
  const kandidaten = leereKandidaten();
  for (const k of KATEGORIEN) {
    kandidaten[k] = sortiereKandidaten(entdoppele([...umfeld.kandidaten[k], ...gesucht[k]]));
  }

  const beste: Partial<Record<NotfallKategorie, StellenKandidat>> = {};
  for (const k of KATEGORIEN) {
    const treffer = besterVorschlag(kandidaten[k]);
    if (treffer) beste[k] = treffer;
  }
  if (suchFehler && !KATEGORIEN.some(k => beste[k])) throw new Error(suchFehler);

  // Telefonlücken nur für ungeprüfte Treffer — ein bestätigter Eintrag ohne
  // Nummer hat keine, und die KI soll ihn nicht überschreiben.
  const luecken = KATEGORIEN
    .map(kategorie => ({ kategorie, kandidat: beste[kategorie] }))
    .filter((l): l is { kategorie: NotfallKategorie; kandidat: StellenKandidat } =>
      !!l.kandidat && !l.kandidat.telefon && l.kandidat.quelle === 'osm');
  if (luecken.length) melde?.('Fehlende Telefonnummern werden nachgeschlagen …');
  const kiGefuellt = await telefoneNachschlagen(luecken);

  const kontakte: NotrufAutoErgebnis['kontakte'] = {};
  for (const k of KATEGORIEN) {
    const treffer = beste[k];
    if (treffer) kontakte[FELD[k]] = kandidatZuKontakt(treffer);
  }

  melde?.('Kartenausschnitt wird erstellt …');
  // Karte zeigt Projekt + ALLE VIER Stellen. Der Durchgangsarzt fehlte hier —
  // er stand als Kontakt im Notrufplan, hatte aber keinen Punkt auf der Karte.
  // Farbe und Kuerzel haengen an der Kategorie, damit das Weglassen einer
  // Stelle die uebrigen Marker nicht umfaerbt (vorher index-basiert).
  const stellen: KartenPunkt[] = KARTEN_MARKER
    .map(m => ({ marker: m, treffer: beste[m.kategorie] }))
    .filter((e): e is { marker: typeof KARTEN_MARKER[number]; treffer: StellenKandidat } => !!e.treffer)
    .map(e => ({ lat: e.treffer.lat, lng: e.treffer.lng, farbe: e.marker.farbe, kuerzel: e.marker.kuerzel }));
  const zoom = autoZoom(center, stellen);
  let kartenBild: string | undefined;
  try {
    kartenBild = await buildStaticMap(center, stellen, zoom);
  } catch {
    kartenBild = undefined; // Karte optional — Stellen sind das Wichtige
  }

  const fehlende = KATEGORIEN.filter(k => !beste[k]).map(k => KATEGORIE_LABEL[k]);
  const vorbehalt = KATEGORIEN.filter(k => beste[k]?.eignung === 'eingeschraenkt')
    .map(k => `${KATEGORIE_LABEL[k]} (${beste[k]!.hinweis || 'unsicher'})`);

  let status = 'Fertig. Bitte alle Angaben prüfen und bestätigen.';
  if (ausDatenbank.length) {
    status += ` ✓ Ohne Suche übernommen: ${ausDatenbank.map(k => KATEGORIE_LABEL[k]).join(', ')}.`;
  }
  if (fehlende.length) status += ` Nicht gefunden: ${fehlende.join(', ')} (manuell ergänzen).`;
  if (vorbehalt.length) status += ` ⚠ Nur als Vorschlag: ${vorbehalt.join(', ')}.`;
  if (kiGefuellt.length) {
    status += ` ⚠ Telefon per KI geschätzt: ${kiGefuellt.map(k => KATEGORIE_LABEL[k]).join(', ')}. Unbedingt verifizieren.`;
  }
  if (suchFehler) status += ` ⚠ Umkreissuche unvollständig: ${suchFehler}`;
  if (!umfeld.erreichbar) status += ' (Geprüfte Stellen waren nicht abrufbar.)';
  status += ' © OpenStreetMap-Mitwirkende.';

  return { geo: center, kontakte, kandidaten, stellen, zoom, kartenBild, status, ausDatenbank };
};
